import type { NitroFetchOptions, NitroFetchRequest } from 'nitropack'
import {
  getNeedNotFoundMessage,
  isNeedNotFoundFailed,
  throwNotFoundError,
} from '~/utils/request'


const DEFAULT_TIMEOUT = 120000;

type RequestApiOptions = {
  headers?: Record<string, any>
  timeout?: number
  baseApi?: string
  signal?: AbortSignal
  needNotFound?: boolean
  skipToken?: boolean
  responseCode401SignOut?: boolean
  unwrapData?: boolean
  [key: string]: any
}

type JcFetchOptions<T extends NitroFetchRequest = NitroFetchRequest> =
  NitroFetchOptions<T> & {
    jcOpts?: {
      needNotFound?: boolean
      skipToken?: boolean
      responseCode401SignOut?: boolean
      unwrapData?: boolean
    }
  }

function safeUseNuxtApp() {
  try {
    return useNuxtApp() as any
  } catch (error) {
    return null
  }
}

export const useUseRequest = () => {
  const requestByJcFetch = <T>(
    url: string,
    requestOptions: JcFetchOptions = {},
    apiOptions: RequestApiOptions = {},
  ) => {
    const nuxtApp = safeUseNuxtApp()
    const jcFetch = nuxtApp?.$jcFetch as
      | (<R = any>(request: string, options?: JcFetchOptions) => Promise<R>)
      | undefined
    const mergedOptions: JcFetchOptions = {
      ...requestOptions,
      timeout:
        apiOptions.timeout ??
        requestOptions.timeout ??
        DEFAULT_TIMEOUT,
      ...(apiOptions.baseApi ? { baseURL: apiOptions.baseApi } : {}),
      ...(apiOptions.signal ? { signal: apiOptions.signal } : {}),
      ...(apiOptions.headers
        ? {
            headers: {
              ...(requestOptions.headers || {}),
              ...apiOptions.headers,
            },
          }
        : {}),
      jcOpts: {
        needNotFound: apiOptions.needNotFound ?? false,
        skipToken: apiOptions.skipToken,
        responseCode401SignOut: apiOptions.responseCode401SignOut,
        unwrapData: apiOptions.unwrapData,
      },
    }

    if (jcFetch) {
      return jcFetch<T>(url, mergedOptions)
    }

    const rawFetch = $fetch as <R = any>(
      request: string,
      options?: any,
    ) => Promise<R>
    const { jcOpts, ...rawOptions } = mergedOptions
    return rawFetch<T>(url, rawOptions)
      .then((res: any) => {
        if (jcOpts?.needNotFound && isNeedNotFoundFailed(res)) {
          throwNotFoundError(getNeedNotFoundMessage(res))
        }
        return res
      })
      .catch((err: any) => {
        if (jcOpts?.needNotFound) {
          if (err?.statusCode === 404) {
            throw err
          }
          throwNotFoundError(
            getNeedNotFoundMessage(err?.data || err, err?.message),
          )
        }
        throw err
      })
  }

  const fetch = <T>(
    url: string,
    requestOptions: JcFetchOptions = {},
    apiOptions: RequestApiOptions = {},
  ) => {
    return requestByJcFetch<T>(url, requestOptions, apiOptions)
  }

  const get = <T>(
    url: string,
    params = {},
    options: RequestApiOptions = {},
  ) => {
    return fetch<T>(
      url,
      {
        method: 'GET',
        params,
      },
      options,
    )
  }

  const getAsset = <T>(
    url: string,
    params = {},
    options: RequestApiOptions = {},
  ) => {
    return fetch<T>(
      url,
      {
        method: 'GET',
        params,
      },
      {
        ...options,
        baseApi: 'https://sapi.jctrans.com',
      },
    )
  }

  const post = <T>(
    url: string,
    body = {},
    params = {},
    options: RequestApiOptions = {},
  ) => {
    return fetch<T>(
      url,
      {
        method: 'POST',
        body,
        params,
      },
      options,
    )
  }

  return {
    get,
    post,
    getAsset,
    fetch,
  }
}
